/** @jsx jsx */
import React, { type MouseEvent, type ReactNode, useState, useCallback } from 'react';
import { jsx } from '@compiled/react';
import { SOFTWARE_PROJECT } from '@jira/platform__project-types/src/project-types.tsx';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import Button, { CustomThemeButton } from '@atlaskit/button';
import Heading from '@atlaskit/heading';
import CrossIcon from '@atlaskit/icon/core/cross';
import PremiumIcon from '@atlaskit/icon/core/premium';
import Link from '@atlaskit/link';
import { Box, Inline, Stack, xcss, Text } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { fg } from '@atlassian/jira-feature-gating';
import { JiraPopup as Popup } from '@atlassian/jira-popup/src/ui/jira-popup.tsx';
import { LEARN_MORE_URL } from '@atlassian/jira-atlassian-intelligence/src/common/constants.tsx';
import { AIButton } from '@atlassian/jira-atlassian-intelligence/src/common/ui/ai-button/index.tsx';
import { useEntryPointButtonTrigger } from '@atlassian/jira-entry-point-button-trigger/src/index.tsx';
import { ModalEntryPointContainer } from '@atlassian/jira-entry-point-modal-container/src/index.tsx';
import { useEntryPoint } from '@atlassian/jira-entry-point/src/controllers/use-entry-point/index.tsx';
import AIUpsellBanner from '@atlassian/jira-illustrations/src/ui/adg4/jira/other/components/ai-upsell-banner/index.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { entryPointPremiumUpgradeModal } from '@atlassian/jira-premium-upgrade-modal/src/entrypoint.tsx';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { isStandardEdition } from '@atlassian/jira-project-archive-trigger/src/ui/utils.tsx';
import { useAppEditions } from '@atlassian/jira-tenant-context-controller/src/components/app-editions/index.tsx';
import { useIsSiteAdmin } from '@atlassian/jira-tenant-context-controller/src/components/is-site-admin/index.tsx';
import messages from './messages.tsx';

type DialogContentsProps = {
	onClose: (event: MouseEvent, analyticsEvent: UIAnalyticsEvent) => void;
	buttonTrigger: React.Ref<HTMLElement>;
};

const DialogContents = ({ onClose, buttonTrigger }: DialogContentsProps) => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	return (
		<Box xcss={backgroundBorderStyles}>
			<Stack xcss={containerStyles}>
				<Inline xcss={imageWrapperStyles}>
					<Box xcss={imageStyles}>
						<AIUpsellBanner alt={formatMessage(messages.bannerAltText)} />
					</Box>
					<Box xcss={closeButtonWrapperStyles}>
						<Button appearance="subtle" spacing="none" onClick={onClose}>
							<CrossIcon label={formatMessage(messages.closeModal)} color={token('color.icon')} />
						</Button>
					</Box>
				</Inline>
				<Stack alignBlock="start" space="space.100" xcss={contentContainerStyles}>
					<Heading size="small">{formatMessage(messages.heading)}</Heading>
					<Text>
						{formatMessage(
							fg('jira-issue-terminology-update-m4')
								? messages.descriptionSporkIssueTerminologyRefreshM4
								: messages.descriptionSpork,
							{
								link: (...chunks: ReactNode[]) => (
									<Link
										target="_blank"
										href={LEARN_MORE_URL}
										onClick={() =>
											fireUIAnalytics(
												createAnalyticsEvent({}),
												'link clicked',
												'atlassianIntelligenceLearnMore',
											)
										}
									>
										{chunks}
									</Link>
								),
							},
						)}
					</Text>
					<Inline alignInline="end" xcss={buttonContainerStyles}>
						<CustomThemeButton
							ref={buttonTrigger}
							onClick={onClose}
							iconBefore={
								<PremiumIcon
									label={formatMessage(messages.premiumIconLabel)}
									color={token('color.text.inverse')}
								/>
							}
							theme={(current, themeProps) => ({
								buttonStyles: {
									...current(themeProps).buttonStyles,
									display: 'flex',
									alignItems: 'center',
									background: `linear-gradient(135deg, ${token('color.background.discovery.bold')}, ${token('color.background.brand.bold')})`,
									outline: 'none',
									boxSizing: 'border-box',
									padding: `${token('space.050')} ${token('space.150')}`,
									borderRadius: token('radius.small'),
									height: token('space.400'),
									border: 'none',
									gap: token('space.025'),
								},
								spinnerStyles: current(themeProps).spinnerStyles,
							})}
						>
							<Text size="medium" weight="medium" color="color.text.inverse">
								{formatMessage(messages.tryItFreeButton)}
							</Text>
						</CustomThemeButton>
					</Inline>
				</Stack>
			</Stack>
		</Box>
	);
};

const entryPointParams = {};
export const AIPremiumUpsellButtonPopup = (): JSX.Element | null => {
	const [isOpen, setIsOpen] = useState(false);
	const isSiteAdmin = useIsSiteAdmin();
	const appEditions = useAppEditions();
	const isSoftwareAppStandardEdition = isStandardEdition(SOFTWARE_PROJECT, appEditions);

	const { entryPointActions, entryPointReferenceSubject } = useEntryPoint(
		entryPointPremiumUpgradeModal,
		entryPointParams,
	);
	const tryItFreeButtonTrigger = useEntryPointButtonTrigger(entryPointActions);

	const onAIButtonClick = useCallback(
		(_: MouseEvent, analyticsEvent: UIAnalyticsEvent) => {
			if (!isOpen) {
				fireUIAnalytics(analyticsEvent, 'button clicked', 'jqlBuilderAiTogglePremiumUpsell');
			}
			setIsOpen((open) => !open);
		},
		[isOpen, setIsOpen],
	);

	const onTryItFreeButtonClick = useCallback(
		(_: MouseEvent, analyticsEvent: UIAnalyticsEvent) => {
			fireUIAnalytics(analyticsEvent, 'button clicked', 'upgradeToPremiumFromJqlAiUpsell');
			setIsOpen(false);
		},
		[setIsOpen],
	);

	// We only want to show the AI Premium Upsell button to Standard Edition Site Admins
	if (!isSiteAdmin || !isSoftwareAppStandardEdition) {
		return null;
	}

	return (
		<>
			<Popup
				isOpen={isOpen}
				onClose={() => setIsOpen(false)}
				content={() => (
					<DialogContents onClose={onTryItFreeButtonClick} buttonTrigger={tryItFreeButtonTrigger} />
				)}
				trigger={(triggerProps) => <AIButton {...triggerProps} onClick={onAIButtonClick} />}
				placement="bottom-start"
				messageId="jql-builder-with-ai.ui.ai-premium-upsell-button-popup.popup.ai-premium-upsell-button"
				messageType="marketing"
			/>
			<ModalEntryPointContainer
				entryPointReferenceSubject={entryPointReferenceSubject}
				id="jira-premium-upgrade-modal"
				packageName="jiraPremiumUpgradeModal"
				errorFallback="flag"
				runtimeProps={{ onClose: entryPointActions.unload }}
				entryPointActions={entryPointActions}
			/>
		</>
	);
};

const backgroundBorderStyles = xcss({
	overflow: 'hidden',
	boxShadow: 'elevation.shadow.overlay',
	borderRadius: 'radius.small',
	width: '450px',
	padding: 'space.025',
	position: 'relative',
	background: `linear-gradient(135deg, ${token('color.background.accent.blue.subtle')}, ${token('color.background.accent.purple.subtle')})`,
});

const containerStyles = xcss({
	borderRadius: 'radius.small',
	backgroundColor: 'elevation.surface.overlay',
});

const imageWrapperStyles = xcss({
	borderTopLeftRadius: 'radius.small',
	borderTopRightRadius: 'radius.small',
});

const imageStyles = xcss({
	objectFit: 'cover',
	width: '100%',
	borderTopLeftRadius: 'radius.small',
	borderTopRightRadius: 'radius.small',
	borderBottomLeftRadius: '0',
	borderBottomRightRadius: '0',
});

const closeButtonWrapperStyles = xcss({
	position: 'absolute',
	top: '0',
	right: '0',
	padding: 'space.200',
});

const contentContainerStyles = xcss({
	padding: 'space.300',
	alignSelf: 'stretch',
});

const buttonContainerStyles = xcss({
	width: '100%',
	marginTop: 'space.100',
});
